import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Row, Col, Toast, Badge } from 'react-bootstrap';
import { FaPlus, FaTrash, FaEdit, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { getSlidersByPageCode, addSlider, deleteSlider, updateSlider } from '../../services/sliderService';
import SliderForm from './SliderForm';

const SliderList = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showForm, setShowForm] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await getSlidersByPageCode('MainPage');
      console.log('Sliders response in component:', response);
      
      // API direkt olarak slider array'i dönüyor
      if (Array.isArray(response)) {
        setSliders(response);
        setError(null);
      } else if (response.isSuccess === false) {
        setError(response.resultMessage || 'Sliderlar yüklenirken bir hata oluştu.');
        setSliders([]);
      } else {
        setError('Geçersiz response formatı');
        setSliders([]);
      }
    } catch (error) {
      console.error('Error fetching sliders:', error);
      setError('Sliderlar yüklenirken bir hata oluştu.');
      setSliders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setShowForm(true);
    setEditingSlider(null);
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setShowForm(true);
  };

  const handleDelete = async (slider) => {
    if (window.confirm('Bu sliderı silmek istediğinizden emin misiniz?')) {
      try {
        const response = await deleteSlider(slider.guid);
        if (response.isSuccess) {
          setToastVariant('success');
          setToastMessage('Slider başarıyla silindi');
          setShowToast(true);
          fetchSliders();
        } else {
          setToastVariant('danger');
          setToastMessage(response.resultMessage || 'Slider silinirken bir hata oluştu');
          setShowToast(true);
        }
      } catch (error) {
        setToastVariant('danger');
        setToastMessage('Slider silinirken bir hata oluştu');
        setShowToast(true);
      }
    }
  };

  const handleSubmit = async (formData) => {
    try {
      let response;
      if (editingSlider) {
        // Güncelleme
        response = await updateSlider({
          guid: editingSlider.guid,
          ...formData
        });
      } else {
        // Ekleme
        response = await addSlider(formData);
      }

      if (response.isSuccess) {
        setToastVariant('success');
        setToastMessage(editingSlider ? 'Slider başarıyla güncellendi' : 'Slider başarıyla eklendi');
        setShowToast(true);
        setShowForm(false);
        setEditingSlider(null);
        fetchSliders();
      } else {
        setToastVariant('danger');
        setToastMessage(response.resultMessage || (editingSlider ? 'Slider güncellenirken bir hata oluştu' : 'Slider eklenirken bir hata oluştu'));
        setShowToast(true);
      }
    } catch (error) {
      setToastVariant('danger');
      setToastMessage(editingSlider ? 'Slider güncellenirken bir hata oluştu' : 'Slider eklenirken bir hata oluştu');
      setShowToast(true);
    }
  };

  return (
    <Container fluid className="py-3">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Slider Yönetimi</h5>
          <Button variant="primary" onClick={handleAdd}>
            <FaPlus className="me-2" /> Yeni Slider Ekle
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-danger">{error}</div>
          ) : (
            <Row xs={1} md={2} lg={3} className="g-4">
              {sliders.map((slider, index) => (
                <Col key={slider.guid}>
                  <Card>
                    <Card.Img
                      variant="top"
                      src={slider.fileUrl}
                      style={{ height: '200px', objectFit: 'cover' }}
                      alt="Slider"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '';
                      }}
                    />
                    <Card.Body>
                      <div className="mb-2">
                        <Badge bg="primary" className="me-2">
                          Sıra: {slider.indexOrder}
                        </Badge>
                        {slider.url && (
                          <Badge bg="info">
                            URL: {slider.url.length > 30 ? slider.url.substring(0, 30) + '...' : slider.url}
                          </Badge>
                        )}
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">
                          Gösterim Süresi: {(slider.intervalSecond / 1000).toFixed(1)}s
                        </small>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="btn-group" role="group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(slider)}
                            title="Düzenle"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(slider)}
                            title="Sil"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 1000
        }}
        bg={toastVariant}
      >
        <Toast.Body className="text-white">{toastMessage}</Toast.Body>
      </Toast>

      <SliderForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingSlider(null);
        }}
        onSubmit={handleSubmit}
        editingSlider={editingSlider}
      />
    </Container>
  );
};

export default SliderList;
